/**
 * The room as a guest sees it, at one second of the talk: the player with its chapters and pins, and the moment or the
 * offer under it. The computer's walk draws it with its words typeable in place; the phone's and iPad's draw it plain.
 */
import type { ComponentProps, CSSProperties, MouseEvent, ReactElement } from "react";
import { BOARD_CAPS } from "../../../shared/board-caps";
import type { BoardCard, BoardCards, BoardState } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import type { BoardHost } from "../host";
import { chapterRowsOf } from "../make/MakeChapters";
import { mmss } from "../make/time";
import { Editable } from "./Editable";
import { useNoteMedia } from "./noteMedia";
import { bareUrl, fileFace, fileThumb, KIND, type Moment, momentsOf, momentWords, offerOf, onScreen, roomDuration, useRoomFiles, WORDS } from "./roomParts";

/** What typing on the room does, when it can be typed on. */
export interface RoomEdits {
  typeOn: (id: string, patch: Record<string, unknown>) => void;
  typedOn: (id: string, back: (card: BoardCard) => Record<string, unknown>) => void;
  /** A part's own classes: open (`bw-sel`). */
  marks: (key: string) => string;
  openPart: (e: MouseEvent<HTMLElement>, key: string) => void;
}

/** Words drawn where the room is only looked at. */
function Plain({ as: As = "span", className, value }: ComponentProps<typeof Editable>): ReactElement {
  return <As className={className}>{value}</As>;
}

const nothing = () => undefined;

export function RoomPreview({ state, host, at, phone, title, edits }: { state: BoardState; host: BoardHost; at: number; phone: boolean; title?: string; edits?: RoomEdits }) {
  const lib = useRoomFiles(state);
  const Words: typeof Editable = edits ? Editable : Plain;
  const typeOn = edits?.typeOn ?? nothing;
  const typedOn = edits?.typedOn ?? nothing;
  const marks = edits?.marks ?? (() => "");
  const openPart = edits?.openPart ?? nothing;
  const moments = momentsOf(state);
  const offer = offerOf(state);
  const chapters = chapterRowsOf(state);
  const live = host.server.scheduleMode === "live";
  const dur = roomDuration(state, host);
  const notePoster = useNoteMedia(offer?.videoNote).poster;
  const brand = host.server.brand;
  const vars = { "--pa": brand.accent, "--pb": brand.brass, "--pt": brand.paper, "--pi": "#241a1e", "--pd": `color-mix(in srgb, ${brand.accent} 18%, #0f0a0c)` } as CSSProperties;
  const hostName = host.server.hostName || "Your host";
  const hostFirst = hostName.split(" ")[0];
  const ci = chapters.length ? Math.max(0, chapters.findLastIndex((c) => c.t <= at)) : -1;
  const { up, showOffer, offerOpen } = onScreen(state, at, live);
  const pct = (s: number) => `${Math.max(0, Math.min(100, (s / dur) * 100)).toFixed(3)}%`;
  const avatar = host.server.hostAvatarUrl ? <img src={host.server.hostAvatarUrl} alt="" /> : <span className="bw-moini">{hostFirst.slice(0, 1)}</span>;
  const momentCard = (m: Moment) => {
    const key = `mo:${m.id}`;
    const words = WORDS[m.kind];
    const file = m.kind === "handout" ? fileFace(lib.assets, m.key) : null;
    const inner =
      m.kind === "poll" ? (
        <div className={cx("bw-mopts", live && "row")}>
          {m.opts.map((o, i) => (
            <span className="bw-mopt" key={i}>
              <i />
              <Words
                value={o}
                label={`Choice ${i + 1}`}
                max={BOARD_CAPS.option}
                onType={(text) => typeOn(m.id, { opts: m.opts.map((x, k) => (k === i ? text : x)) })}
                onDone={(_, was) => typedOn(m.id, (c) => ({ opts: (c as BoardCards["poll"]).opts.map((x, k) => (k === i ? was : x)) }))}
              />
            </span>
          ))}
        </div>
      ) : m.kind === "question" ? (
        <div className="bw-mans">
          <span>Type your answer</span>
          <span className="bw-mbtn2">Send</span>
        </div>
      ) : m.kind === "reaction" ? (
        <div className="bw-mreact">
          {m.emojis.map((e) => (
            <span key={e}>{e}</span>
          ))}
        </div>
      ) : (
        <div className="bw-mfile">
          {fileThumb(file?.pic)}
          <span className="sp">
            <b>{file?.name ?? m.name}</b>
            {file?.sub ? <em>{file.sub}</em> : null}
          </span>
          <span className="bw-mbtn2">
            <Icon name="download" /> Download
          </span>
        </div>
      );
    const question = (
      <Words
        as="b"
        className="bw-moq"
        value={momentWords(m)}
        label={`The ${KIND[m.kind].name.toLowerCase()}’s words`}
        max={words.max}
        onType={(text) => typeOn(m.id, { [words.field]: text })}
        onDone={(_, was) => typedOn(m.id, () => ({ [words.field]: was }))}
      />
    );
    const foot = m.kind === "handout" ? "It stays on their page too." : m.kind === "reaction" ? `${hostFirst} sees who reacted.` : `Only ${hostFirst} sees the answer.`;
    // A live never pauses: the moment comes up in the corner, with the host's name on it, and nothing to skip.
    if (live) {
      return (
        <div className={cx("bw-mo live", marks(key))} data-bw={key} onClick={(e) => openPart(e, key)}>
          <div className="bw-moh">
            {avatar}
            <b><bdi>{hostFirst}</bdi> asks the room</b>
          </div>
          {question}
          {inner}
          <div className="bw-mof">
            <span>{foot}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={cx("bw-mo", marks(key))} data-bw={key} onClick={(e) => openPart(e, key)}>
        <div className="bw-moh">
          {avatar}
          <Icon name="pause" />
          <b>
            {hostFirst} {KIND[m.kind].held}
          </b>
        </div>
        {m.said ? <q className="bw-mosaid">{m.said}</q> : null}
        <small className="bw-mok">
          <Icon name={KIND[m.kind].icon} />
          {KIND[m.kind].tag}
        </small>
        {question}
        {inner}
        <div className="bw-mof">
          <span>{foot}</span>
          <span className="bw-skip">
            Skip <Icon name="chev" />
          </span>
        </div>
      </div>
    );
  };
  const offerFile = offer?.offerType === "download" ? fileFace(lib.assets, offer.file) : null;
  const offerBox = (folded: boolean) =>
    !offer ? null : folded ? (
      <button type="button" className={cx("bw-odock", marks("ofwhen"))} data-bw="ofwhen" onClick={(e) => openPart(e, "ofwhen")}>
        {host.server.hostAvatarUrl ? <img src={host.server.hostAvatarUrl} alt="" /> : null}
        <span><bdi>{hostFirst}</bdi>’s offer</span>
        <span className="bw-obtn">{offer.offerType === "code" ? `${offer.code} · Copy` : offer.btn}</span>
        <Icon name="down" />
      </button>
    ) : (
      <div className={cx("bw-ocard", `t-${offer.offerType}`, marks("ofwhen"))} data-bw="ofwhen" onClick={(e) => openPart(e, "ofwhen")}>
        {offer.videoNote ? (
          <div className="bw-onotev">
            <span className="fl-athumb">{notePoster ? <img src={notePoster} alt="" /> : null}</span>
            <span className="bw-onotep">
              <Icon name="playf" fill />
            </span>
            <span>
              A note from {hostFirst}
              {offer.videoSec ? ` · ${mmss(offer.videoSec)}` : ""}
            </span>
          </div>
        ) : null}
        <div className="bw-ocard-h">
          {host.server.hostAvatarUrl ? <img src={host.server.hostAvatarUrl} alt="" /> : null}
          <div className="sp">
            <q>{offer.said || "Something for you, before you go."}</q>
            <span>
              {hostFirst}, {live ? `${Math.round((offer.t ?? 0) / 60)} minutes in` : `at ${mmss(offer.t ?? 0)}`}
            </span>
          </div>
          <Icon name="x" />
        </div>
        <Words
          as="b"
          className="ph1 bw-owords"
          value={offer.text ?? ""}
          label="The offer’s words"
          empty="Type what this offer says"
            max={BOARD_CAPS.prose}
          onType={(text) => typeOn(offer.id, { text })}
          onDone={(_, was) => typedOn(offer.id, () => ({ text: was }))}
        />
        {offer.offerType === "code" ? (
          <>
            <div className="bw-ocode">
              <Words
                as="b"
                value={offer.code}
                label="The code on the offer"
                max={BOARD_CAPS.offerBtn}
                code
                onType={(code) => typeOn(offer.id, { code })}
                onDone={(_, was) => typedOn(offer.id, () => ({ code: was }))}
              />
              <span className="bw-mbtn2">
                <Icon name="copy" /> Copy
              </span>
            </div>
            {offer.url.trim() ? <div className="bw-onote">Use it at {bareUrl(offer.url)}.</div> : null}
          </>
        ) : offer.offerType === "message" ? (
          <div className="bw-obtn big ghost">
            <Icon name="chat" /> Write back to <bdi>{hostFirst}</bdi>
          </div>
        ) : (
          <>
            {offer.offerType === "download" ? (
              <div className="bw-mfile">
                {fileThumb(offerFile?.pic)}
                <span className="sp">
                  <b>{offerFile?.name ?? (offer.file ? offer.file.split("/").pop() : "No file yet")}</b>
                  {offerFile?.sub ? <em>{offerFile.sub}</em> : null}
                </span>
              </div>
            ) : null}
            <div className="bw-obtn big">
              {offer.offerType === "download" ? <Icon name="download" /> : null}
              <Words
                value={offer.btn}
                label="The button’s words"
                max={BOARD_CAPS.offerBtn}
                onType={(btn) => typeOn(offer.id, { btn })}
                onDone={(_, was) => typedOn(offer.id, () => ({ btn: was }))}
              />
              {offer.offerType === "download" ? null : <Icon name="right" />}
            </div>
          </>
        )}
        {offer.offerType === "message" ? null : <span className="bw-olater">Later</span>}
      </div>
    );

  return (
    <div className={cx("pv bw-room", phone && "phone", up && !live && "held")} style={vars}>
      <div className="bw-rtop">
        <div className="logo">
          <i>{(host.server.workspaceName || hostName).slice(0, 2).toUpperCase()}</i>
          <span>{host.server.workspaceName || hostName}</span>
        </div>
        <div className="bw-rtt">
          <b className="ph1">{title ?? host.server.title}</b>
          <span>{live ? `Live now · ${Math.floor(at / 60)} minutes in` : ci >= 0 ? `Chapter ${ci + 1} · ${chapters[ci].title}` : ""}</span>
        </div>
        {live ? (
          <span className="bw-livep">
            <i />
            Live now
          </span>
        ) : (
          <span className="bw-rpill">
            <Icon name="home" /> Your page
          </span>
        )}
      </div>
      <div className="bw-vid">
        {host.poster ? <img src={host.poster} alt="" /> : <span className="bw-vidblank" />}
        {phone ? null : (
          <>
            {up ? momentCard(up) : null}
            {showOffer ? offerBox(!offerOpen) : null}
          </>
        )}
        <div className="bw-vctl">
          {live ? null : (
            <div className="bw-vscrub">
              {chapters.map((c, i) => {
                const end = i < chapters.length - 1 ? chapters[i + 1].t : dur;
                return (
                  <span key={`${c.t}-${i}`} style={{ flex: `${end - c.t} 1 0` }}>
                    <i style={{ width: `${Math.max(0, Math.min(1, (at - c.t) / Math.max(1, end - c.t))) * 100}%` }} />
                  </span>
                );
              })}
              {moments
                .filter((m) => m.on)
                .map((m) => (
                  <em key={m.id} style={{ left: pct(m.t ?? 0) }} />
                ))}
              {offer ? <em className="of" style={{ left: pct(offer.t ?? 0) }} /> : null}
            </div>
          )}
          <div className="bw-vrow">
            <Icon name={live ? "live" : "playf"} fill={!live} />
            <b>{live ? `${Math.floor(at / 60)}:00` : mmss(at)}</b>
            <span>{live ? "" : `/ ${mmss(dur)}`}</span>
            {live ? <span className="bw-vlive">Live</span> : ci >= 0 ? <span className="bw-vch">{chapters[ci].title}</span> : null}
          </div>
        </div>
      </div>
      {phone ? (
        <div className="bw-phbelow">
          {up ? momentCard(up) : null}
          {showOffer ? offerBox(!offerOpen) : null}
        </div>
      ) : null}
    </div>
  );
}
